import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Eye, Globe, FileText, RotateCcw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface EditModeContextType {
  isEditMode: boolean;
  editingScope: 'event' | 'template';
  toggleEditMode: () => void;
  toggleEditingScope: () => void;
}

const EditModeContext = createContext<EditModeContextType | undefined>(undefined);

export function useEditMode() {
  const context = useContext(EditModeContext);
  if (context === undefined) {
    throw new Error('useEditMode must be used within an EditModeProvider');
  }
  return context;
}

interface EditModeProviderProps {
  children: ReactNode;
  isAdmin?: boolean;
  defaultEditMode?: boolean;
  defaultScope?: 'event' | 'template';
}

export function EditModeProvider({ 
  children, 
  isAdmin = true, 
  defaultEditMode = false, 
  defaultScope = 'event' 
}: EditModeProviderProps) {
  const [isEditMode, setIsEditMode] = useState(defaultEditMode);
  const [editingScope, setEditingScope] = useState<'event' | 'template'>(defaultScope);
  const [user, setUser] = useState<any>(null);
  const [isAnyDialogOpen, setIsAnyDialogOpen] = useState(false);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Hide floating controls when any modal/dialog is open to avoid blocking UI
  useEffect(() => {
    const checkDialogs = () => {
      const openDialog = document.querySelector('[role="dialog"]');
      setIsAnyDialogOpen(!!openDialog);
    };
    checkDialogs();
    const observer = new MutationObserver(checkDialogs);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  const toggleEditingScope = () => {
    setEditingScope(prev => prev === 'event' ? 'template' : 'event');
  };
  return (
    <EditModeContext.Provider value={{ 
      isEditMode,
      editingScope, 
      toggleEditMode, 
      toggleEditingScope 
    }}>

      {/* Floating Edit Controls - visible only for authenticated admins */}
      {(isAdmin !== false && !isAnyDialogOpen) && (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
          {isEditMode && (
            <div className="flex items-center gap-2 bg-background/95 backdrop-blur-sm border rounded-lg p-2">
              <Badge variant={editingScope === 'event' ? 'default' : 'secondary'}>
                <FileText className="h-3 w-3 mr-1" />
                Event
              </Badge>
              <Button
                onClick={toggleEditingScope}
                variant="ghost"
                size="sm"
                className="p-1 h-6 w-6"
              >
                <RotateCcw className="h-3 w-3" />
              </Button>
              <Badge variant={editingScope === 'template' ? 'default' : 'secondary'}>
                <Globe className="h-3 w-3 mr-1" />
                Template
              </Badge>
            </div>
          )}
          
          <Button
            onClick={toggleEditMode}
            className={`shadow-lg ${isEditMode ? 'bg-orange-500 hover:bg-orange-600' : 'bg-primary hover:bg-primary/90'}`}
            size="lg"
          >
            {isEditMode ? (
              <>
                <Eye className="h-5 w-5 mr-2" />
                Preview Mode
              </>
            ) : (
              <>
                <Edit className="h-5 w-5 mr-2" />
                Edit Mode
              </>
            )}
          </Button>
        </div>
      )}
      {children}
    </EditModeContext.Provider>
  );
}